import React from 'react';
import Modal from 'react-bootstrap/Modal';
import creditCardType from 'credit-card-type';
import { apiPost } from '../../utils/api';
import { PAYMENT_REVIEW_MESSAGE, shouldBlockPaymentRetry } from '../../utils/paymentRetrySafety';
import CancellationTermsAgreement from '../Common-components/CancellationTermsAgreement';
import NavTermsNRefundsContent from '../Common-components/NavigatorBar/NavigatorContents/navTermsNRefundsContent';
import useCancellationAgreement from '../../hooks/useCancellationAgreement';
import { resolveTermsByLineId } from '../../pages/Booking/termsData';

const SUPPORTED_CARD_TYPES = ['VIS', 'MSC', 'AMX'];
const WOWCRUISE_BILLING_CONTACT = {
  country: 'KR',
  county: 'Seoul',
  homecity: 'Guro-gu',
  postcode: '08377',
  address1: '55, Digital-ro 34-gil, Kolon Science Valley II, B201-F12',
  address2: '',
};

function mapCardType(brand) {
  const key = String(brand || '').toLowerCase().replace(/[^a-z]/g, '');
  switch (key) {
    case 'visa':
      return 'VIS';
    case 'mastercard':
      return 'MSC';
    case 'americanexpress':
    case 'amex':
      return 'AMX';
    default:
      return '';
  }
}

function parseAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatAmount(value) {
  const amount = parseAmount(value);
  if (amount === null) return '$0.00';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function normalizeEnglishName(value) {
  return String(value || '').replace(/[^a-zA-Z]/g, '').toUpperCase();
}

function normalizeCardNumber(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 19);
}

function normalizeNumeric(value, maxLength = 4) {
  return String(value || '').replace(/\D/g, '').slice(0, maxLength);
}

function normalizePartialAmountInput(value) {
  const sanitized = String(value || '').replace(/[^\d.]/g, '');
  const parts = sanitized.split('.');
  if (parts.length <= 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`;
}

function luhnCheck(numStr) {
  const digits = String(numStr || '').replace(/\D/g, '');
  if (!digits) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

function toFourDigitYear(value) {
  const digits = normalizeNumeric(value, 4);
  if (digits.length === 4) return Number(digits);
  if (digits.length === 2) return 2000 + Number(digits);
  return 0;
}

function resolvePassengerTitle(passenger) {
  const title = String(passenger?.title || '').trim();
  if (title) return title;

  const gender = String(passenger?.gender || '').trim().toLowerCase();
  if (gender === 'male' || gender === 'm') return 'Mr';
  if (gender === 'female' || gender === 'f') return 'Ms';
  return 'Mr';
}

function ReservationPaymentModal({
  show,
  onHide,
  reservationDetail,
  paymentAction,
  onPaymentSuccess,
}) {
  const primaryPassenger = Array.isArray(reservationDetail?.pax_list)
    ? reservationDetail.pax_list[0] || null
    : null;
  const defaultLastName = String(primaryPassenger?.last_name || '').trim().toUpperCase();
  const defaultFirstName = String(primaryPassenger?.first_name || '').trim().toUpperCase();
  const defaultTitle = resolvePassengerTitle(primaryPassenger);
  const termsSections = resolveTermsByLineId(reservationDetail?.line_id);
  const agreementScope = JSON.stringify([
    reservationDetail?.reservation_idx ?? null,
    reservationDetail?.line_id ?? null,
    termsSections,
  ]);
  const { agreed, setAgreed } = useCancellationAgreement(agreementScope, show === true);

  const [paymentOption, setPaymentOption] = React.useState('full');
  const [partialAmount, setPartialAmount] = React.useState('');
  const [cardInfo, setCardInfo] = React.useState({
    lastName: defaultLastName,
    firstName: defaultFirstName,
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    csc: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [blockedPayments, setBlockedPayments] = React.useState(() => new Set());
  const paymentReservationKey = String(reservationDetail?.reservation_idx ?? '');
  const paymentRetryBlocked = blockedPayments.has(paymentReservationKey) || paymentAction?.reason === 'PAYMENT_REVIEW_REQUIRED';
  const [didSubmit, setDidSubmit] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [termsOpen, setTermsOpen] = React.useState(false);
  const termsPanelRef = React.useRef(null);
  const submittingRef = React.useRef(null);
  const cycleRef = React.useRef(0);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  React.useEffect(() => {
    const cycle = ++cycleRef.current;
    setTermsOpen(false);
    return () => { cycleRef.current = cycle + 1; };
  }, [show, agreementScope]);

  React.useEffect(() => {
    if (!termsOpen || !show) return;
    termsPanelRef.current?.focus();
    termsPanelRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [termsOpen, show]);

  React.useEffect(() => {
    if (!show) return;
    setPaymentOption('full');
    setPartialAmount('');
    setIsSubmitting(submittingRef.current !== null);
    setDidSubmit(false);
    setSubmitError('');
    setCardInfo({
      lastName: defaultLastName,
      firstName: defaultFirstName,
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      csc: '',
    });
  }, [show, defaultLastName, defaultFirstName]);

  const maxAmount = React.useMemo(() => parseAmount(paymentAction?.max_amount), [paymentAction?.max_amount]);
  const selectedAmount = React.useMemo(() => {
    if (paymentAction?.allow_partial === true && paymentOption === 'partial') {
      return parseAmount(partialAmount);
    }
    return maxAmount;
  }, [maxAmount, partialAmount, paymentAction?.allow_partial, paymentOption]);

  const cardNumberDigits = React.useMemo(
    () => normalizeCardNumber(cardInfo.cardNumber),
    [cardInfo.cardNumber]
  );
  const detectedCard = React.useMemo(() => {
    if (!cardNumberDigits) return null;
    const matches = creditCardType(cardNumberDigits);
    return Array.isArray(matches) && matches.length > 0 ? matches[0] : null;
  }, [cardNumberDigits]);
  const mappedCardType = React.useMemo(
    () => mapCardType(detectedCard?.type),
    [detectedCard?.type]
  );

  const amountError = React.useMemo(() => {
    if (!paymentAction?.available) return '현재 결제할 수 없는 예약입니다.';
    if (paymentAction?.allow_partial !== true || paymentOption !== 'partial') return '';
    if (partialAmount.trim() === '') return '부분결제 금액을 입력해주세요.';

    const amount = parseAmount(partialAmount);
    if (amount === null || amount <= 0) return '부분결제 금액은 0보다 커야 합니다.';
    if (maxAmount !== null && amount > maxAmount) return '남은 잔금을 초과할 수 없습니다.';
    return '';
  }, [maxAmount, partialAmount, paymentAction?.allow_partial, paymentAction?.available, paymentOption]);

  const fieldErrors = React.useMemo(() => {
    const errors = {
      lastName: '',
      firstName: '',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      csc: '',
    };

    if (!cardInfo.lastName.trim()) {
      errors.lastName = '성을 입력해주세요.';
    }
    if (!cardInfo.firstName.trim()) {
      errors.firstName = '이름을 입력해주세요.';
    }

    if (!cardNumberDigits) {
      errors.cardNumber = '카드번호를 입력해주세요.';
    } else if (!mappedCardType || !SUPPORTED_CARD_TYPES.includes(mappedCardType)) {
      errors.cardNumber = '지원하지 않는 카드입니다. Visa/Master/Amex만 사용 가능합니다.';
    } else {
      const allowedLengths = Array.isArray(detectedCard?.lengths) && detectedCard.lengths.length > 0
        ? detectedCard.lengths
        : [13, 15, 16, 19];
      if (!allowedLengths.includes(cardNumberDigits.length)) {
        errors.cardNumber = '카드번호 자릿수를 확인해주세요.';
      } else if (!luhnCheck(cardNumberDigits)) {
        errors.cardNumber = '유효하지 않은 카드번호입니다.';
      }
    }

    if (!/^\d{2}$/.test(cardInfo.expiryMonth)) {
      errors.expiryMonth = '월(MM) 2자리를 입력해주세요.';
    } else {
      const month = Number(cardInfo.expiryMonth);
      if (month < 1 || month > 12) {
        errors.expiryMonth = '월은 01~12 사이여야 합니다.';
      }
    }

    if (!/^\d{2,4}$/.test(cardInfo.expiryYear)) {
      errors.expiryYear = '연도(YY 또는 YYYY)를 입력해주세요.';
    }

    const cscLength = mappedCardType === 'AMX' ? 4 : 3;
    if (!new RegExp(`^\\d{${cscLength}}$`).test(cardInfo.csc)) {
      errors.csc = mappedCardType === 'AMX'
        ? 'CID 4자리를 입력해주세요.'
        : 'CVC/CVV 3자리를 입력해주세요.';
    }

    return errors;
  }, [
    cardInfo.csc,
    cardInfo.expiryMonth,
    cardInfo.expiryYear,
    cardInfo.firstName,
    cardInfo.lastName,
    cardNumberDigits,
    detectedCard?.lengths,
    mappedCardType,
  ]);

  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  const canSubmit = paymentAction?.available === true
    && agreed
    && !hasFieldErrors
    && !amountError
    && selectedAmount !== null
    && selectedAmount > 0;

  const handleFieldChange = React.useCallback((event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === 'lastName' || name === 'firstName') {
      nextValue = normalizeEnglishName(value);
    } else if (name === 'cardNumber') {
      nextValue = normalizeCardNumber(value);
    } else if (name === 'expiryMonth') {
      nextValue = normalizeNumeric(value, 2);
    } else if (name === 'expiryYear') {
      nextValue = normalizeNumeric(value, 4);
    } else if (name === 'csc') {
      nextValue = normalizeNumeric(value, mappedCardType === 'AMX' ? 4 : 3);
    }

    setCardInfo((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  }, [mappedCardType]);

  const handlePaymentOptionChange = React.useCallback((event) => {
    const option = event.target.value;
    setPaymentOption(option);
    if (option !== 'partial') {
      setPartialAmount('');
    }
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (submittingRef.current || !show || paymentRetryBlocked) return;
    setDidSubmit(true);
    setSubmitError('');

    if (!agreed) {
      setSubmitError('취소료 규정을 확인하고 동의해주세요.');
      return;
    }
    if (!canSubmit || selectedAmount === null) {
      return;
    }

    const reservationIdx = Number(reservationDetail?.reservation_idx ?? 0);
    const lineId = Number(reservationDetail?.line_id ?? 0);
    if (!Number.isFinite(reservationIdx) || reservationIdx <= 0 || !Number.isFinite(lineId) || lineId <= 0) {
      setSubmitError('결제할 예약 정보를 확인할 수 없습니다.');
      return;
    }

    const ccardPayload = {
      amount: selectedAmount,
      nameoncard: `${cardInfo.lastName} ${cardInfo.firstName}`.trim(),
      cardtype: mappedCardType,
      cardnumber: cardNumberDigits,
      expirymonth: String(cardInfo.expiryMonth).padStart(2, '0'),
      expiryyear: toFourDigitYear(cardInfo.expiryYear),
      signature: cardInfo.csc,
      title: defaultTitle,
      firstname: cardInfo.firstName,
      lastname: cardInfo.lastName,
      postcode: WOWCRUISE_BILLING_CONTACT.postcode,
      address1: WOWCRUISE_BILLING_CONTACT.address1,
      address2: WOWCRUISE_BILLING_CONTACT.address2,
      homecity: WOWCRUISE_BILLING_CONTACT.homecity,
      county: WOWCRUISE_BILLING_CONTACT.county,
      country: WOWCRUISE_BILLING_CONTACT.country,
    };

    const request = { cycle: cycleRef.current };
    submittingRef.current = request;
    const isCurrentRequest = () => mountedRef.current && cycleRef.current === request.cycle;
    setIsSubmitting(true);
    let paymentSucceeded = false;
    try {
      const paymentResponse = await apiPost('/reservation/pay_reservation.php', {
        reservation_idx: reservationIdx,
        line_id: lineId,
        payment_context: 'reservation_detail',
        cancellation_terms_agreed: true,
        ccard: ccardPayload,
      });

      if (!isCurrentRequest()) return;

      if (paymentResponse?.success !== true) {
        const error = new Error(paymentResponse?.message || '결제 요청에 실패했습니다.');
        error.response = { status: 200, data: paymentResponse };
        throw error;
      }
      paymentSucceeded = true;

      if (typeof onPaymentSuccess === 'function') {
        await onPaymentSuccess(paymentResponse);
      }
      if (isCurrentRequest()) onHide();
    } catch (error) {
      if (!isCurrentRequest()) return;
      const requiresReview = shouldBlockPaymentRetry(error, paymentSucceeded);
      if (requiresReview) setBlockedPayments(previous => new Set([...previous, paymentReservationKey]));
      const message = requiresReview ? PAYMENT_REVIEW_MESSAGE : (error?.response?.data?.message || error?.message || '결제 요청에 실패했습니다.');
      setSubmitError(message);
    } finally {
      if (submittingRef.current === request) submittingRef.current = null;
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [
    paymentReservationKey,
    paymentRetryBlocked,
    canSubmit,
    agreed,
    show,
    cardInfo.csc,
    cardInfo.expiryMonth,
    cardInfo.expiryYear,
    cardInfo.firstName,
    cardInfo.lastName,
    cardNumberDigits,
    defaultTitle,
    mappedCardType,
    onHide,
    onPaymentSuccess,
    reservationDetail?.line_id,
    reservationDetail?.reservation_idx,
    selectedAmount,
  ]);

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : onHide}
      centered
      className='reservation-payment-modal'
      dialogClassName='reservation-payment-modal-dialog'
      contentClassName='reservation-payment-modal-content'
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{paymentAction?.title || '결제'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='reservation-payment-summary'>
          <div className='reservation-payment-summary-row'>
            <span>결제 유형</span>
            <strong>{paymentAction?.title || '-'}</strong>
          </div>
          <div className='reservation-payment-summary-row'>
            <span>남은 총 결제금액</span>
            <strong>{formatAmount(paymentAction?.remaining_total_amount)}</strong>
          </div>
          {paymentAction?.stage === 'deposit' && (
            <div className='reservation-payment-summary-row'>
              <span>이번 결제금액</span>
              <strong>{formatAmount(paymentAction?.default_amount)}</strong>
            </div>
          )}
          {paymentAction?.stage === 'balance' && (
            <div className='reservation-payment-summary-row'>
              <span>남은 잔금</span>
              <strong>{formatAmount(paymentAction?.remaining_balance_amount)}</strong>
            </div>
          )}
          {paymentAction?.description ? (
            <p className='reservation-payment-summary-note'>{paymentAction.description}</p>
          ) : null}
        </div>

        {paymentAction?.allow_partial === true && (
          <div className='reservation-payment-option-group'>
            <label className='reservation-payment-option'>
              <input
                type='radio'
                name='reservation-payment-option'
                value='full'
                checked={paymentOption === 'full'}
                onChange={handlePaymentOptionChange}
                disabled={isSubmitting}
              />
              <span>잔금 전액결제</span>
              <strong>{formatAmount(paymentAction?.max_amount)}</strong>
            </label>
            <label className='reservation-payment-option'>
              <input
                type='radio'
                name='reservation-payment-option'
                value='partial'
                checked={paymentOption === 'partial'}
                onChange={handlePaymentOptionChange}
                disabled={isSubmitting}
              />
              <span>잔금 부분결제</span>
            </label>
            {paymentOption === 'partial' && (
              <div className='reservation-payment-partial-input'>
                <label htmlFor='reservation-payment-partial-amount'>부분결제 금액(USD)</label>
                <input
                  id='reservation-payment-partial-amount'
                  type='text'
                  inputMode='decimal'
                  value={partialAmount}
                  onChange={(event) => setPartialAmount(normalizePartialAmountInput(event.target.value))}
                  placeholder='0.00'
                  disabled={isSubmitting}
                />
                <p className='reservation-payment-hint'>
                  0보다 크고 {formatAmount(paymentAction?.max_amount)} 이하로 입력할 수 있습니다.
                </p>
                {didSubmit && amountError ? (
                  <p className='reservation-payment-error'>{amountError}</p>
                ) : null}
              </div>
            )}
          </div>
        )}

        <div className='reservation-payment-form'>
          <div className='reservation-payment-form-grid two-column'>
            <label>
              <span>성(영문)</span>
              <input
                type='text'
                name='lastName'
                value={cardInfo.lastName}
                onChange={handleFieldChange}
                disabled={isSubmitting}
                maxLength={30}
              />
              {didSubmit && fieldErrors.lastName ? (
                <p className='reservation-payment-error'>{fieldErrors.lastName}</p>
              ) : null}
            </label>
            <label>
              <span>이름(영문)</span>
              <input
                type='text'
                name='firstName'
                value={cardInfo.firstName}
                onChange={handleFieldChange}
                disabled={isSubmitting}
                maxLength={30}
              />
              {didSubmit && fieldErrors.firstName ? (
                <p className='reservation-payment-error'>{fieldErrors.firstName}</p>
              ) : null}
            </label>
          </div>

          <label>
            <span>카드번호</span>
            <input
              type='text'
              name='cardNumber'
              inputMode='numeric'
              value={cardInfo.cardNumber}
              onChange={handleFieldChange}
              disabled={isSubmitting}
              placeholder='숫자만 입력'
            />
            <p className='reservation-payment-hint'>Visa / Mastercard / American Express</p>
            {didSubmit && fieldErrors.cardNumber ? (
              <p className='reservation-payment-error'>{fieldErrors.cardNumber}</p>
            ) : null}
          </label>

          <div className='reservation-payment-form-grid'>
            <label>
              <span>유효기간 월</span>
              <input
                type='text'
                name='expiryMonth'
                inputMode='numeric'
                value={cardInfo.expiryMonth}
                onChange={handleFieldChange}
                disabled={isSubmitting}
                placeholder='MM'
              />
              {didSubmit && fieldErrors.expiryMonth ? (
                <p className='reservation-payment-error'>{fieldErrors.expiryMonth}</p>
              ) : null}
            </label>
            <label>
              <span>유효기간 연도</span>
              <input
                type='text'
                name='expiryYear'
                inputMode='numeric'
                value={cardInfo.expiryYear}
                onChange={handleFieldChange}
                disabled={isSubmitting}
                placeholder='YY'
              />
              {didSubmit && fieldErrors.expiryYear ? (
                <p className='reservation-payment-error'>{fieldErrors.expiryYear}</p>
              ) : null}
            </label>
            <label>
              <span>{mappedCardType === 'AMX' ? 'CID' : 'CVC/CVV'}</span>
              <input
                type='password'
                name='csc'
                inputMode='numeric'
                value={cardInfo.csc}
                onChange={handleFieldChange}
                disabled={isSubmitting}
                placeholder={mappedCardType === 'AMX' ? '4자리' : '3자리'}
              />
              {didSubmit && fieldErrors.csc ? (
                <p className='reservation-payment-error'>{fieldErrors.csc}</p>
              ) : null}
            </label>
          </div>
        </div>

        {termsOpen && (
          <section className='reservation-payment-terms' aria-label='적용 취소료 규정' tabIndex={-1} ref={termsPanelRef}>
            <h3>적용 취소료 규정</h3>
            <NavTermsNRefundsContent term={termsSections} />
          </section>
        )}
      </Modal.Body>
      <Modal.Footer className='reservation-payment-modal-footer'>
        <div className='reservation-payment-footer'>
          <div className='reservation-payment-total'>
            <span>이번 결제금액</span>
            <strong>{formatAmount(selectedAmount)}</strong>
          </div>
          {(submitError || paymentRetryBlocked) ? (
            <p className='reservation-payment-error submit'>{paymentRetryBlocked ? PAYMENT_REVIEW_MESSAGE : submitError}</p>
          ) : null}
          {paymentAction?.retry_notice ? (
            <p className='reservation-payment-hint'>{paymentAction.retry_notice}</p>
          ) : null}
          <CancellationTermsAgreement
            checked={agreed}
            onChange={setAgreed}
            disabled={isSubmitting}
            onViewTerms={() => setTermsOpen(true)}
          />
          <div className='reservation-payment-actions'>
            <button
              type='button'
              className='btn reservation-payment-cancel-btn'
              onClick={onHide}
              disabled={isSubmitting}
            >
              닫기
            </button>
            <button
              type='button'
              className='btn reservation-payment-submit-btn'
              onClick={handleSubmit}
              disabled={isSubmitting || !agreed || paymentRetryBlocked}
            >
              {isSubmitting ? '결제 진행 중...' : `${formatAmount(selectedAmount)} 결제하기`}
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default ReservationPaymentModal;
